import {
    enabledMigrationObjectTypes,
    isMigrationObjectTypeEnabled,
    MigrationSyncFlags,
} from "@common/helpers/accountingMigrationObjectTypes";
import { MigrationObjectType } from "@common/types/domains/accounting/accountingMigration";
import {
    MIGRATION_OBJECT_TYPE_LABELS,
    MIGRATION_OBJECT_TYPE_PREREQUISITES,
} from "@components/accountingMigration/historicalData";
import { joinWithAnd } from "@helpers/joinWithAnd";

const REFRESH_CASCADE_PREREQUISITES: ReadonlyMap<
    MigrationObjectType,
    ReadonlySet<MigrationObjectType>
> = new Map(
    Array.from(MIGRATION_OBJECT_TYPE_PREREQUISITES.entries()).map(
        ([objectType, prerequisites]) => [
            objectType,
            new Set(
                Array.from(prerequisites).filter(
                    (prerequisite) =>
                        prerequisite !== MigrationObjectType.GENERAL_LEDGER,
                ),
            ),
        ],
    ),
);

// The backend also refreshes synced classes with any listed invoices or bills.
const SYNCED_REFRESH_PREREQUISITES: ReadonlyMap<
    MigrationObjectType,
    ReadonlySet<MigrationObjectType>
> = new Map([
    [MigrationObjectType.INVOICES, new Set([MigrationObjectType.CLASSES])],
    [MigrationObjectType.BILLS, new Set([MigrationObjectType.CLASSES])],
]);

function refreshPrerequisitesOf({
    objectType,
    migration,
}: {
    objectType: MigrationObjectType;
    migration: MigrationSyncFlags;
}): ReadonlySet<MigrationObjectType> {
    const syncedPrerequisites = Array.from(
        SYNCED_REFRESH_PREREQUISITES.get(objectType) ?? [],
    ).filter((prerequisite) =>
        isMigrationObjectTypeEnabled(migration, prerequisite),
    );
    return new Set([
        ...(REFRESH_CASCADE_PREREQUISITES.get(objectType) ?? []),
        ...syncedPrerequisites,
    ]);
}

function getCascadeLockedObjectTypes({
    selection,
    migration,
}: {
    selection: ReadonlySet<MigrationObjectType>;
    migration: MigrationSyncFlags;
}): Set<MigrationObjectType> {
    const locked = new Set<MigrationObjectType>();
    for (const objectType of REFRESH_CASCADE_PREREQUISITES.keys()) {
        if (!selection.has(objectType)) {
            continue;
        }
        for (const prerequisite of refreshPrerequisitesOf({
            objectType,
            migration,
        })) {
            locked.add(prerequisite);
        }
    }
    return locked;
}

function getMigrationLockedObjectTypes(
    migration: MigrationSyncFlags,
): ReadonlySet<MigrationObjectType> {
    if (migration.syncGeneralLedger) {
        return new Set();
    }
    const locked = new Set<MigrationObjectType>([
        MigrationObjectType.GENERAL_LEDGER,
    ]);
    for (const [
        objectType,
        prerequisites,
    ] of MIGRATION_OBJECT_TYPE_PREREQUISITES) {
        if (prerequisites.has(MigrationObjectType.GENERAL_LEDGER)) {
            locked.add(objectType);
        }
    }
    return locked;
}

export function initialRefreshSelection(
    migration: MigrationSyncFlags,
): ReadonlySet<MigrationObjectType> {
    return new Set(enabledMigrationObjectTypes(migration));
}

export function toggleRefreshObjectType({
    selection,
    objectType,
    migration,
}: {
    selection: ReadonlySet<MigrationObjectType>;
    objectType: MigrationObjectType;
    migration: MigrationSyncFlags;
}): ReadonlySet<MigrationObjectType> {
    const isSelected = selection.has(objectType);
    if (isSelected) {
        const isLocked = getCascadeLockedObjectTypes({
            selection,
            migration,
        }).has(objectType);
        if (isLocked) {
            return selection;
        }
        const next = new Set(selection);
        next.delete(objectType);
        return next;
    }

    if (getMigrationLockedObjectTypes(migration).has(objectType)) {
        return selection;
    }

    const next = new Set(selection);
    next.add(objectType);
    for (const prerequisite of refreshPrerequisitesOf({
        objectType,
        migration,
    })) {
        next.add(prerequisite);
    }
    return next;
}

export function lockedRefreshObjectTypes({
    selection,
    migration,
}: {
    selection: ReadonlySet<MigrationObjectType>;
    migration: MigrationSyncFlags;
}): ReadonlySet<MigrationObjectType> {
    const locked = getCascadeLockedObjectTypes({ selection, migration });
    for (const objectType of getMigrationLockedObjectTypes(migration)) {
        locked.add(objectType);
    }
    return locked;
}

export function refreshPrerequisiteReason({
    selection,
    objectType,
    migration,
}: {
    selection: ReadonlySet<MigrationObjectType>;
    objectType: MigrationObjectType;
    migration: MigrationSyncFlags;
}): string | undefined {
    if (
        !migration.syncGeneralLedger &&
        MIGRATION_OBJECT_TYPE_PREREQUISITES.get(objectType)?.has(
            MigrationObjectType.GENERAL_LEDGER,
        )
    ) {
        return "Needs the general ledger, which this migration doesn't sync";
    }

    const dependentLabels: string[] = [];
    for (const dependent of REFRESH_CASCADE_PREREQUISITES.keys()) {
        if (
            selection.has(dependent) &&
            refreshPrerequisitesOf({ objectType: dependent, migration }).has(
                objectType,
            )
        ) {
            dependentLabels.push(MIGRATION_OBJECT_TYPE_LABELS[dependent]);
        }
    }
    if (dependentLabels.length === 0) {
        return undefined;
    }
    return `Needed by ${joinWithAnd(dependentLabels)}`;
}
